/**
 * ReturnToChip — global "← Back to <Module>" pill.
 *
 * Rendered inside the black GoogleTranslateBar utility strip (mounted
 * once at the App-shell level, above every route), the chip gives the
 * visitor a one-click way home when they're on a stand-alone entry
 * point like /signup, /admin/login, /forgot-password.
 *
 * Target-resolution order (first hit wins):
 *   1. ?from=<encoded-URL>            — stamped by cross-module links
 *      in the "Other Modules" dropdown / suite outbound anchors.
 *   2. sessionStorage[RETURN_KEY]     — sticks the target across
 *      further navigations inside the destination module.
 *   3. sessionStorage[LAST_NON_AUTH_KEY]
 *                                     — updated on EVERY react-router
 *      location change to the last non-auth path the visitor was on.
 *      This handles pure SPA clicks (Link to /signup) where the
 *      browser never sets document.referrer.
 *   4. document.referrer              — fallback for full-page loads
 *      arriving from a trusted Hustle-family host or same-origin
 *      non-auth path.
 *
 * All candidates must satisfy isTrustedHost() (or be same-origin
 * non-auth) — we never echo an unvetted URL into a clickable link.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const RETURN_KEY = "hs:return_to";
const LAST_NON_AUTH_KEY = "hs:last_non_auth_path";
const AUTH_PATH_RX = /^\/(signup|register|login|admin\/login|forgot-password)/;

/* Only allow return URLs pointing back at the Hustle module family +
 * gethustleconnect. Anything else is discarded silently — we never
 * echo an unvetted URL into a clickable link. */
function isTrustedHost(host) {
  if (!host) return false;
  const h = host.toLowerCase();
  if (h.endsWith("gethustleconnect.com")) return true;
  return /(^|\.)hustle[a-z0-9-]*\.(com|au|net|org|io|co|xyz)(\.[a-z]+)?$/.test(h);
}

/* Turn "hustlemeetups.com" → "HustleMeetups", "gethustleconnect.com" →
 * "HustleConnect", "hustleshop.au" → "HustleShop", etc. Falls back to
 * the raw hostname if nothing sensible can be derived. */
function friendlyLabel(host) {
  const clean = (host || "").replace(/^www\./, "").toLowerCase();
  if (clean.endsWith("gethustleconnect.com")) return "HustleConnect";
  const m = clean.match(/hustle([a-z0-9]+)/i);
  if (m) return "Hustle" + m[1].charAt(0).toUpperCase() + m[1].slice(1);
  return clean || "Previous site";
}

/* Encode the current in-app location as an absolute URL — used by the
 * "last non-auth path" tracker. */
function currentAbsoluteHref(loc) {
  if (typeof window === "undefined") return null;
  return (
    window.location.origin +
    (loc.pathname || "/") +
    (loc.search || "") +
    (loc.hash || "")
  );
}

/* Resolve a validated stored URL string, or null. */
function readStored(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const u = new URL(raw);
    if (isTrustedHost(u.hostname) || u.hostname === window.location.hostname) {
      return u.href;
    }
  } catch {}
  return null;
}

export default function ReturnToChip() {
  const location = useLocation();
  const [target, setTarget] = useState(null);

  // Track the last non-auth path the visitor was on. Runs on every
  // react-router location change, so a pure SPA click from "/" to
  // "/signup" still leaves a breadcrumb behind (unlike
  // document.referrer, which the browser DOES NOT set for
  // client-side navigation).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = location.pathname || "/";
    if (!AUTH_PATH_RX.test(path)) {
      try {
        sessionStorage.setItem(LAST_NON_AUTH_KEY, currentAbsoluteHref(location));
      } catch {}
    }
  }, [location.pathname, location.search, location.hash]);

  // Resolve the chip target on every navigation.
  useEffect(() => {
    if (typeof window === "undefined") return;

    // 1. ?from=<encoded-URL> (highest priority — a suite link
    //    explicitly told us where to go back to).
    let raw = null;
    try {
      const url = new URL(window.location.href);
      raw = url.searchParams.get("from");
      if (raw) {
        // Strip the param so it doesn't leak into share/copy URLs.
        url.searchParams.delete("from");
        const search = url.searchParams.toString();
        const newHref =
          url.pathname + (search ? `?${search}` : "") + url.hash;
        window.history.replaceState({}, "", newHref);
      }
    } catch {}

    let stored = null;
    if (raw) {
      try {
        const parsed = new URL(raw);
        if (
          isTrustedHost(parsed.hostname) ||
          parsed.hostname === window.location.hostname
        ) {
          stored = parsed.href;
          try { sessionStorage.setItem(RETURN_KEY, stored); } catch {}
        }
      } catch {}
    }

    // 2. Previous RETURN_KEY (sticks across further nav inside the
    //    destination). Re-validate — an older component version may
    //    have stored a value we no longer accept.
    if (!stored) stored = readStored(RETURN_KEY);

    const onAuthPage = AUTH_PATH_RX.test(window.location.pathname || "");

    // 3. Last non-auth path (auth pages only — this is the SPA
    //    tracker so authenticated flows know where the visitor came
    //    from within the same domain).
    if (!stored && onAuthPage) {
      const last = readStored(LAST_NON_AUTH_KEY);
      if (last) {
        try {
          const u = new URL(last);
          // Never point back at an auth page.
          if (!AUTH_PATH_RX.test(u.pathname)) stored = u.href;
        } catch {}
      }
    }

    // 4. document.referrer — fires on EVERY page (not just auth) so a
    //    cross-module hop (hustlesuite.au → hustleeats.au) lights up
    //    the "← Back to HustleSuite" chip on the destination. On
    //    non-auth pages we only trust CROSS-domain referrers to avoid
    //    showing a chip that points back to the same site.
    if (!stored && document.referrer) {
      try {
        const ref = new URL(document.referrer);
        const refAuthy = AUTH_PATH_RX.test(ref.pathname);
        const sameOrigin = ref.hostname === window.location.hostname;
        const trusted = isTrustedHost(ref.hostname);
        // Auth pages: same-origin referrers welcome (they're the SPA
        // origin doing a full reload). Non-auth pages: only accept
        // cross-domain trusted-host referrers.
        if (
          !refAuthy &&
          ((onAuthPage && (trusted || sameOrigin)) ||
            (!onAuthPage && trusted && !sameOrigin))
        ) {
          stored = ref.href;
        }
      } catch {}
    }

    if (stored) {
      try { sessionStorage.setItem(RETURN_KEY, stored); } catch {}
    }
    setTarget(stored);
  }, [location.pathname, location.search]);

  if (!target) return null;

  const host = (() => {
    try { return new URL(target).hostname; } catch { return ""; }
  })();
  const label = friendlyLabel(host);

  const clear = () => {
    try { sessionStorage.removeItem(RETURN_KEY); } catch {}
  };

  return (
    <a
      href={target}
      data-testid="global-return-chip"
      onClick={clear}
      title={`Return to ${host}`}
      rel="noopener"
      className="inline-flex items-center gap-1 text-[11px] sm:text-[12px]
                 text-[#FAF5E8] bg-white/15 hover:bg-white/25
                 rounded-full h-5 px-1.5 sm:px-2 whitespace-nowrap
                 transition-colors shrink-0"
    >
      <ArrowLeft className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
      <span className="hidden sm:inline">Back to&nbsp;</span>
      <span className="font-semibold">{label}</span>
    </a>
  );
}
